// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Colours } from '../../../../base-styles/colours';
import { SectorColours } from '../../../../base-styles/sector-colours';
import { hasSelectedTE } from '../../../../functions/client-model/client-node/has-selected-te';
import { holdsActiveCustomModel } from '../../../../functions/client-model/client-node/holds-active-custom-model';
import { holdsCustomModel } from '../../../../functions/client-model/client-node/holds-custom-model';
import { holdsInitialisedUtilisationTE } from '../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { isLeafNode } from '../../../../functions/client-model/client-node/is-leaf-node';
import { dynamicRound } from '../../../../functions/dynamic-round';
import { getUnit } from '../../../../functions/get-unit';
import { isNodeAchievable } from '../../../../functions/is-node-achievable';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { convertUnit } from '../../../../functions/prefixed-unit/convert-unit';
import { getTransitionYearsFromTimeline } from '../../../../functions/timeline/get-transition-years-from-timeline';
import { transformUnit } from '../../../../functions/transform-unit';
import { transitionElementToSector } from '../../../../functions/transition-target-to-sector';
import { stringToSector } from '../../../../functions/transition-targets/string-to-sector';
import { BoardTimeline } from '../../../../model/board-timeline';
import { ClientNode } from '../../../../model/client-model/client-node';
import { Dict } from '../../../../model/dict';
import { Language } from '../../../../model/enums/language';
import { MeasurementSystem } from '../../../../model/enums/measurement-system';
import { WeightMetric } from '../../../../model/prefixed-unit/weight.metric';
import { Sector } from '../../../../model/sector';
import { PersistedTransitionProperties } from '../../../../model/transition-targets/persisted/persisted-transition-properties';
import { groupColourToSector } from '../../../../modules/diagram/components/tree-diagram/group-color-to-sector';
import { YearValues } from '../../../../modules/diagram/components/year-by-year-diagram/visualisers/year-by-year-visualiser/data/year-values';
import { getChildren } from '../../../../public/dashboard/shared/functions/get-children';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { DashboardColours } from '../../../../public/dashboard/shared/model/dashboard-colours';
import { DashboardNodeLabelsV3 } from '../../../../public/dashboard/v3/dashboard-node-labels-v3';
import { DashboardNodeV3 } from '../../../../public/dashboard/v3/dashboard-node-v3';
import { DashboardV3 } from '../../../../public/dashboard/v3/dashboard-v3';
import { DashboardYearDataV3 } from '../../../../public/dashboard/v3/dashboard-year-data-v3';
import { CCCModelUtility } from '../../../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import { FormatNumberService } from '../../../../services/format-number.service';
import { TranslationService } from '../../../../services/translation-service';
import { UnitConversionService } from '../../../../services/unit-conversion/unit-conversion.service';
import { TransitionTargetSector } from '../../../editor-targets-list/components/board-targets/sectors';
import { getLeafNodes } from '../../functions/get-leaf-nodes';
import { getNodes } from '../../functions/get-nodes';
import { sectorToDashboardColour } from '../../functions/sector-to-dashboard-colour';
import { toIdDict } from '../../functions/to-id-dict';
import { YearByYearDataSeries } from './helpers/year-by-year-data-series';
import { YearByYearDataSeriesBuilder } from './helpers/year-by-year-data-series.builder';
import { YearByYearService } from './year-by-year/year-by-year.service';

import { childrenHaveEmissions, getSummarizedEmissions } from '../../../../functions/node-list/historical-emissions';
import { getBoardYearsFromTimeline } from '../../../../functions/timeline/get-board-years-from-timeline';
import { ClientFeatureFlagName } from '../../../../model/client-model/client-feature-flag';
import { UnaddressedEmissionsDisplay } from '../../../../model/client-model/settings/board-settings/unaddressed-emissions-display';
import { TransitionElement } from '../../../../model/enums/transition-element';
import { YearByYearDataService } from '../../../../modules/diagram/components/year-by-year-diagram/services/year-by-year-diagram-data.service';
import { Series } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series';
import { SeriesData } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series-data';
import { UNADDRESSED_EMISSIONS_ID } from '../../../../public/dashboard/shared/dashboard.constants';
import { dashboardStyles } from '../../../../public/dashboard/v4/shared/dashboard.styles';
import { BackendFeatureFlagsService } from '../../../../services/backend/backend-feature-flags.service';
import { Tree } from '../../../../services/backend/model/tree';
import {
  ModelTranslationOutputType,
  generateModelTranslationKey,
} from '../../../../services/climate-engine/ccc-model-utility/generate-model-translation-key';
import { convertValuesToGrams } from '../../functions/convert-values-to-grams';
import { getYearValuesFromDict } from '../../functions/get-year-values-from-dict';
import { ModelsTargetType } from '../../../../api/core/models';

@Injectable({
  providedIn: 'root',
})
export class DashboardV3Service {
  constructor(
    private _cccModelUtility: CCCModelUtility,
    private _featureFlagsService: BackendFeatureFlagsService,
    private _formatNumberService: FormatNumberService,
    private _translationService: TranslationService,
    private _unitConversionService: UnitConversionService,
    private _yearByYearService: YearByYearService,
    private _yearByYearDataService: YearByYearDataService
  ) {}
  toDashboard(board: ClientBoardContainer, ttIconDatas: Dict<string>, tree?: Tree): DashboardV3 {
    const isPanorama = this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama);
    const lang: Language = board.boardSettings.internationalisation.defaultLanguage;
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const years = getTransitionYearsFromTimeline(timeline);
    const boardYears = getBoardYearsFromTimeline(timeline);
    const highlightedYear = timeline.scenarioStartYear;
    const highlightedYearIndex: number = boardYears.indexOf(highlightedYear);
    const endYear = years?.[years.length - 1] ?? highlightedYear;
    const startYear = years?.[0] ?? highlightedYear;

    const measurementSystem = board.boardSettings.measurementSystem
      ? board.boardSettings.measurementSystem
      : MeasurementSystem.Metric;

    const allNodes = getNodes(board);
    const { yearByYearNodeData: yearByYear, totalStartyearEmissions: totalStartYearEmissions } =
      this._yearByYearService.create(board, isPanorama, tree);
    if (!board.boardSettings.totalEmissions) {
      // Published boards have totalEmissions set so will not reset that
      board.boardSettings.totalEmissions = totalStartYearEmissions;
    }
    const historicalEmissions = this._createHistoricalEmissionDict(board, allNodes);
    const unitTranslator = (tt: TransitionElement, properties: PersistedTransitionProperties) => {
      /*
      * Saving this for when series display other values
      const isParameterUpdate = properties.weightType === ModelsTargetType.ParameterUpdate;
      const unit = isParameterUpdate
        ? transformUnit(
            this._cccModelUtility.getPrimaryUpdatedParameterUnit(tt),
            measurementSystem,
            this._unitConversionService
          )
      : transformUnit(
          this._cccModelUtility.getDefaultActivityUnit(tt),
          measurementSystem,
          this._unitConversionService
        );
      */
      const unit = transformUnit(
        this._cccModelUtility.getDefaultActivityUnit(tt),
        measurementSystem,
        this._unitConversionService
      );
      const key = generateModelTranslationKey(ModelTranslationOutputType.Unit, unit);
      return this._translationService.translate(lang, key);
    };
    const kindTranslator = (te: TransitionElement, properties: PersistedTransitionProperties) => {
      /*
      * Saving this for when series display other values
      const isParameterUpdate = properties.weightType === ModelsTargetType.ParameterUpdate;
      let kind = '';
      if (isParameterUpdate) {
        const key = generateModelTranslationKey(
          ModelTranslationOutputType.VariableTitle,
          this._cccModelUtility.getPrimaryUpdatedVariable(te)
        );
        kind = this._translationService.translate(lang, key);
      } else {
        const key = generateModelTranslationKey(ModelTranslationOutputType.TransitionElementMethodTo, te);
        kind = this._translationService.translate(lang, key);
      }
      */
      const key = generateModelTranslationKey(ModelTranslationOutputType.TransitionElementMethodTo, te);
      const kind = this._translationService.translate(lang, key);
      return `${unitTranslator(te, properties)} ${kind}`;
    };
    const progressLabel = this._translationService.translate(lang, UI_STRINGS.YEAR_BY_YEAR_DIAGRAM.PROGRESS);
    const historicalEmissionsLabel = this._translationService.translate(
      lang,
      UI_STRINGS.YEAR_BY_YEAR_DIAGRAM.HISTORICAL_EMISSIONS
    );

    const parentIds: Dict<string> = this._createParentIdDict(board, allNodes);
    const emissionLabelFormatter = (value: number) => {
      const unit = getUnit(value);
      const convertedValue = convertUnit(WeightMetric.Gram, unit, value);
      let { decimals, roundedValue } = dynamicRound(convertedValue, convertedValue);
      if (roundedValue === 0) {
        decimals = 0;
      }
      const formattedValue = this._formatNumberService.formatWithBoardLanguage(lang, roundedValue, decimals);
      return this._translationService.formatWithUnit(
        lang,
        formattedValue,
        this._unitConversionService.transformLabelUnit(unit, measurementSystem)
      );
    };
    const builder = new YearByYearDataSeriesBuilder(
      board,
      yearByYear,
      historicalEmissions,
      parentIds,
      unitTranslator,
      kindTranslator,
      timeline,
      progressLabel,
      historicalEmissionsLabel,
      this._cccModelUtility,
      this._unitConversionService,
      measurementSystem,
      this._translationService.translate(lang, UI_STRINGS.YEAR_BY_YEAR_DIAGRAM.TARGET),
      this._translationService.translate(lang, UI_STRINGS.YEAR_BY_YEAR_DIAGRAM.PROJECTED_EMISSION_REDUCTION)
    );
    const dataSeries: YearByYearDataSeries = builder.getData(board);
    const createLabels = (yearData: DashboardYearDataV3, diagramTitle?) => {
      const startValue = yearData.highlighted.remainingEmissions;
      const endValue = yearData.end.remainingEmissions;
      const pathwayLanelId = this._getPathwayLabelId(startValue, endValue);
      const pathwayReductionLabel =
        pathwayLanelId === UI_STRINGS.DASHBOARD.TRANSITION_PATHWAY_UNCHANGED_LABEL
          ? this._translationService.translate(
              lang,
              pathwayLanelId,
              startValue === 0 ? '0' : emissionLabelFormatter(startValue),
              highlightedYear,
              endYear
            )
          : this._translationService.translate(
              lang,
              pathwayLanelId,
              startValue === 0 ? '0' : emissionLabelFormatter(startValue),
              highlightedYear,
              endValue === 0 ? '0' : emissionLabelFormatter(endValue),
              endYear
            );
      const labels: DashboardNodeLabelsV3 = {
        highlightedYearRemainingEmissionsLabel: emissionLabelFormatter(startValue),
        endYearRemainingEmissionsLabel: emissionLabelFormatter(endValue),
        diagramTitle,
        pathwayReductionLabel,
      };
      return labels;
    };
    const colourDict: Dict<DashboardColours> = {};
    allNodes.forEach((n) => {
      colourDict[n.id] = this._colourForNode(board, n);
    });
    colourDict[board.id] = {
      main: Colours.emissionColour,
      dark: Colours.emissionColourDark,
      light: Colours.emissionColour,
    };
    const nodes: Dict<DashboardNodeV3> = toIdDict(
      allNodes.map((node) => {
        const nodeColours = colourDict[node.id];
        let ttIconData: string | undefined = undefined;
        if (isLeafNode(node)) {
          const defaultIconId = 'default';
          const iconId = hasSelectedTE(node) ? node.nodeProperties.transitionElement : defaultIconId;
          ttIconData = ttIconDatas[iconId] ?? ttIconDatas[defaultIconId];
        }
        const dataSeries = builder.getData(node);
        const yearData: DashboardYearDataV3 = this._createYearData(
          dataSeries.netCarbonAbatement.data.map((d) => d.value),
          dataSeries.emissionBau.data.map((d) => d.value),
          highlightedYearIndex
        );

        const diagramData = this._yearByYearDataService.create(
          node,
          dataSeries,
          colourDict,
          dashboardStyles.colours.dashboardContentPaneBackgroundColour,
          highlightedYearIndex,
          isPanorama
        );

        let diagramTitle: string | undefined = undefined;

        if (holdsCustomModel(node)) {
          diagramTitle = node.nodeProperties?.customTargetModelData?.diagramDescription;
        } else if (holdsInitialisedUtilisationTE(node)) {
          const key = generateModelTranslationKey(
            node.nodeProperties.transitionTargetData?.transitionProperties?.weightType ===
              ModelsTargetType.ParameterUpdate
              ? ModelTranslationOutputType.TransitionElementParameterUpdateLeverDescription
              : ModelTranslationOutputType.TransitionElementActvityShiftDescription,
            node.nodeProperties.transitionElement
          );

          diagramTitle = key ? this._translationService.translate(lang, key) : '';
        }
        return {
          ...node,
          nodeData: dataSeries,
          yearData,
          labels: createLabels(yearData, diagramTitle),
          colours: nodeColours,
          parentId: parentIds[node.id],
          leafNodes: getLeafNodes(board, node).map((n) => n.id),
          ttIconData,
          diagramData,
        };
      })
    );
    const bauTotal = dataSeries.emissionBau.data.map((d) => d.value);
    const bauForHighlightedYear = bauTotal[highlightedYearIndex];
    const unaddressedEmissions = Math.max((board.boardSettings.totalEmissions ?? 0) - bauForHighlightedYear, 0);
    if (
      unaddressedEmissions > 0 &&
      board.boardSettings.unaddressedEmissionsDisplay === UnaddressedEmissionsDisplay.TotalBarAndDiagrams
    ) {
      //This is done outside of the series builder since it is temporary and it will only exist on the board.

      const unaddressedSeries = this._createUnaddressedEmissionSeries(
        lang,
        boardYears,
        startYear,
        unaddressedEmissions
      );

      const addUnaddressedToSeries = (series: Series) => {
        return {
          ...series,
          data: series.data.map((d) => ({ ...d, value: d.value ? d.value + unaddressedEmissions : undefined })),
        };
      };
      dataSeries.projectedEmissionReduction = addUnaddressedToSeries(dataSeries.projectedEmissionReduction);
      dataSeries.emissionBau = addUnaddressedToSeries(dataSeries.emissionBau);

      if (dataSeries.childRemainingEmissions) {
        dataSeries.childRemainingEmissions = [unaddressedSeries, ...dataSeries.childRemainingEmissions];
      }
      if (dataSeries.childRemainingEmissionsAndAbatement) {
        dataSeries.childRemainingEmissionsAndAbatement = [
          unaddressedSeries,
          ...dataSeries.childRemainingEmissionsAndAbatement,
        ];
      }
      if (dataSeries.leafNodeRemainingEmissions) {
        dataSeries.leafNodeRemainingEmissions = [unaddressedSeries, ...dataSeries.leafNodeRemainingEmissions];
      }
      colourDict[unaddressedSeries.id] = {
        main: Colours.unaddressed.main,
        dark: Colours.unaddressed.dark,
        light: Colours.unaddressed.light,
      };
    }
    const boardDiagramData = this._yearByYearDataService.create(
      board,
      dataSeries,
      colourDict,
      dashboardStyles.colours.dashboardContentPaneBackgroundColour,
      highlightedYearIndex
    );
    const boardYearData = this._createYearData(
      dataSeries.netCarbonAbatement.data.map((d) => d.value),
      dataSeries.emissionBau.data.map((d) => d.value),
      highlightedYearIndex
    );
    let totalEmissionsInPlan = Math.max(board.boardSettings.totalEmissions, bauForHighlightedYear);
    return {
      ...board,
      nodeData: dataSeries,
      yearData: boardYearData,
      labels: {
        ...createLabels(boardYearData),
        totalEmissionsLabel: emissionLabelFormatter(totalEmissionsInPlan ?? 0),
        unaddressedEmissionsLabel: emissionLabelFormatter(unaddressedEmissions),
      },
      nodes,
      leafNodes: getLeafNodes(board).map((n) => n.id),
      colours: {
        main: Colours.emissionColour,
        dark: Colours.emissionColourDark,
        light: Colours.emissionColour,
      },
      years,
      highlightedYear,
      endYear,
      startYear,
      indicators: board.indicators,
      diagramData: boardDiagramData,
      unaddressedEmissions,
      totalEmissionsInPlan,
    };
  }

  private _colourForLeafNode(node: ClientNode): DashboardColours {
    let sector: Sector;
    if (holdsCustomModel(node)) {
      sector = stringToSector(
        node.nodeProperties.customTargetModelData
          ? node.nodeProperties.customTargetModelData.sector
          : TransitionTargetSector.TRANSPORT
      );
    } else if (holdsInitialisedUtilisationTE(node)) {
      sector = transitionElementToSector(node.nodeProperties.transitionElement);
    }
    if (sector !== undefined) {
      return sectorToDashboardColour(sector);
    }
    return this._colourForUnknownNode();
  }

  private _colourForNode(board: ClientBoardContainer, node: ClientNode): DashboardColours {
    if (node.nodeProperties.groupColour) {
      const sector = groupColourToSector(Sector.None, node.nodeProperties.groupColour);
      return sectorToDashboardColour(sector);
    }

    if (!isNodeAchievable(node)) {
      return {
        main: Colours.notAchievableNow,
        dark: Colours.notAchievableNowDark,
        light: Colours.notAchievableNow,
      };
    }

    if (isLeafNode(node)) {
      return this._colourForLeafNode(node);
    }

    let colours: DashboardColours[] = getChildren(board, node).reduce((childColours, childNode) => {
      const considerNode =
        !isLeafNode(childNode) || holdsInitialisedUtilisationTE(childNode) || holdsActiveCustomModel(childNode);
      const considerColour = isNodeAchievable(childNode) && considerNode;
      if (considerColour) {
        childColours.push(this._colourForNode(board, childNode));
      }
      return childColours;
    }, []);

    if (colours.length) {
      const firstColour = colours[0];
      const firstMainColour = firstColour.main;
      const mainColours = colours.map((c) => c.main);
      return mainColours.every((s) => s === firstMainColour) ? firstColour : this._colourForUnknownNode();
    }
    return this._colourForUnknownNode();
  }

  private _colourForUnknownNode(): DashboardColours {
    return {
      main: SectorColours.unknownColour,
      dark: Colours.unknownColourDark,
      light: Colours.unknownColourLight,
    };
  }

  private _createHistoricalEmissionDict(board: ClientBoardContainer, nodes: ClientNode[]): Dict<number[]> {
    const dict = {};
    const timeline = board.boardSettings.timeline;
    const years = getBoardYearsFromTimeline(timeline);
    const boardChildNodes = board.childNodes;
    nodes.forEach((node) => {
      if (childrenHaveEmissions({ allNodes: nodes, node, timeline })) {
        dict[node.id] = convertValuesToGrams(
          getYearValuesFromDict(getSummarizedEmissions({ allNodes: nodes, node, timeline }), years, true),
          board
        );
      } else {
        const historicalData = node.nodeProperties.historicalEmissionData;
        const historicalEmissions = historicalData ? getYearValuesFromDict(historicalData, years, true) : undefined;
        if (historicalEmissions) {
          dict[node.id] = convertValuesToGrams(historicalEmissions, board);
        }
      }
    });
    const boardValues = getYearValuesFromDict(
      getSummarizedEmissions({ allNodes: nodes, node: { childNodes: boardChildNodes } as ClientNode, timeline }),
      years,
      true
    );
    dict[board.id] = boardValues ? convertValuesToGrams(boardValues, board) : undefined;
    return dict;
  }

  private _createParentIdDict(board: ClientBoardContainer, nodes: ClientNode[]): Dict<string> {
    return [board, ...nodes].reduce((ids: Dict<string>, item) => {
      if (item.childNodes) {
        item.childNodes.forEach((nodeId) => {
          ids[nodeId] = item.id;
        });
      }
      return ids;
    }, {});
  }
  /**
   * creates a series for the unaddressed emissions in the board
   * this is a temporary function, will be used until we have improved the calculation for targeted emissions and unaddressed emissions.
   */
  private _createUnaddressedEmissionSeries(
    lang: Language,
    boardYears: number[],
    startYear: number,
    unaddressedEmissions: number
  ): Series {
    const data: SeriesData[] = boardYears.map((year) => {
      return {
        label: year + '',
        value: year < startYear ? undefined : unaddressedEmissions,
      };
    });
    return {
      data,
      label: this._translationService.translate(lang, UI_STRINGS.DASHBOARD.UNADDRESSED_EMISSIONS_LABEL),
      id: UNADDRESSED_EMISSIONS_ID,
    };
  }

  private _createYearData(
    abatementData: number[],
    bauData: number[],
    highlightedYearIndex: number
  ): DashboardYearDataV3 {
    return {
      highlighted: this._createYearValues(abatementData, bauData, highlightedYearIndex),
      end: this._createYearValues(abatementData, bauData, abatementData.length - 1),
      start: this._createYearValues(abatementData, bauData, 0),
    };
  }
  private _createYearValues(abatementData: number[], emissionBauData: number[], yearIndex: number): YearValues {
    const abatement = abatementData[yearIndex] ?? 0;
    const emissionBau = emissionBauData[yearIndex] ?? 0;
    return {
      abatement,
      emissionBau,
      remainingEmissions: emissionBau - abatement,
    };
  }

  private _getPathwayLabelId(startValue: number, endValue: number): string {
    if (startValue > endValue) {
      return UI_STRINGS.DASHBOARD.TRANSITION_PATHWAY_REDUCTION_LABEL;
    }
    if (startValue < endValue) {
      return UI_STRINGS.DASHBOARD.TRANSITION_PATHWAY_INCREASE_LABEL;
    }
    return UI_STRINGS.DASHBOARD.TRANSITION_PATHWAY_UNCHANGED_LABEL;
  }
}
